import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, CircleNotch, Check } from "@phosphor-icons/react";
import { AxiosError } from "axios";
import { getReferralConfig, getMyReferrals, createReferral, MyReferral } from "./api";

function badgeFor(r: MyReferral): { label: string; cls: string } {
  if (r.status === "rejected") return { label: "Olmadı", cls: "bg-white/[0.04] text-white/30" };
  if (r.status === "enrolled") return { label: "Kayıt oldu 🎉", cls: "bg-myGreen/15 text-myGreen" };
  if (r.status === "rewarded") return { label: "Ödül ödendi ✓", cls: "bg-myGreen/15 text-myGreen" };
  if (r.application_linked) return { label: "Başvurdu 📝", cls: "bg-myBlue/15 text-myBlue" };
  if (r.invite_sent_at) return { label: "Davet gönderildi ✉️", cls: "bg-white/[0.06] text-white/50" };
  return { label: "Değerlendiriliyor", cls: "bg-white/[0.06] text-white/50" };
}

export default function ReferralPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "" });
  const [kvkkOk, setKvkkOk] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const { data: config } = useQuery({ queryKey: ["referralConfig"], queryFn: getReferralConfig });
  const { data: referrals } = useQuery({ queryKey: ["myReferrals"], queryFn: getMyReferrals });

  const mutation = useMutation({
    mutationFn: createReferral,
    onSuccess: () => {
      setFeedback({ type: "ok", text: "Önerin alındı, arkadaşına davet e-postası gönderildi ✉️" });
      setForm({ name: "", phone: "", email: "", note: "" });
      setKvkkOk(false);
      queryClient.invalidateQueries({ queryKey: ["myReferrals"] });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      setFeedback({ type: "err", text: err.response?.data?.message || "Bir hata oluştu, tekrar dene." });
    },
  });

  const rewardText = config ? `${config.reward_amount.toLocaleString("tr-TR")} TL` : "...";

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Gift size={28} className="text-myGreen" weight="fill" />
        <h1 className="text-xl font-bold text-white">Arkadaşını Öner</h1>
      </div>
      <p className="text-white/50 text-sm mb-6">
        Önerdiğin arkadaşın Code2Work'e kayıt olursa <span className="text-myGreen font-semibold">{rewardText}</span> kazanırsın!
      </p>

      <form
        className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setFeedback(null);
          mutation.mutate({
            name: form.name,
            email: form.email,
            phone: form.phone || undefined,
            note: form.note || undefined,
          });
        }}
      >
        <input
          required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Ad Soyad *" autoComplete="off"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20"
        />
        <input
          required type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="E-posta *" autoComplete="off"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20"
        />
        <input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="Telefon (opsiyonel)" autoComplete="off"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20"
        />
        <textarea
          value={form.note} rows={2}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Not (opsiyonel — örn. üniversiteden arkadaşım, yazılıma ilgili)"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/20 resize-none"
        />
        <label className="flex items-start gap-2.5 text-xs text-white/40 cursor-pointer">
          <input type="checkbox" checked={kvkkOk} onChange={(e) => setKvkkOk(e.target.checked)} className="mt-0.5" />
          <span>Önerdiğim kişinin bilgilerini onun bilgisi dahilinde paylaştığımı onaylıyorum; kendisine Code2Work tarafından bir davet e-postası gönderilecektir.</span>
        </label>
        {feedback && (
          <div className={`text-sm rounded-xl px-4 py-2.5 ${feedback.type === "ok" ? "bg-myGreen/10 text-myGreen" : "bg-red-500/10 text-red-400"}`}>
            {feedback.text}
          </div>
        )}
        <button
          type="submit"
          disabled={!kvkkOk || mutation.isPending}
          className="w-full flex items-center justify-center gap-2 bg-myGreen/90 hover:bg-myGreen disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold rounded-xl py-2.5 text-sm transition-colors cursor-pointer"
        >
          {mutation.isPending ? <CircleNotch size={18} className="animate-spin" /> : <Check size={18} />}
          Öner
        </button>
      </form>

      <h2 className="text-xs uppercase tracking-wider text-white/30 font-medium mt-8 mb-3 px-1">Önerdiklerim</h2>
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl overflow-hidden">
        {!referrals || referrals.length === 0 ? (
          <div className="p-6 text-center text-white/30 text-sm">Henüz kimseyi önermedin.</div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {referrals.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white/80 truncate">{r.referred_name}</div>
                  <div className="text-xs text-white/30">
                    {new Date(r.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(r.status === "enrolled" || r.status === "rewarded") && (
                    <span className="text-xs text-myGreen font-semibold">{r.reward_amount.toLocaleString("tr-TR")} TL</span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded-full ${badgeFor(r).cls}`}>
                    {badgeFor(r).label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
